import { IconChevronLeft, IconRefresh } from "@tabler/icons-react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { faNumber } from "./format";
import classes from "./home.module.scss";

type HeroProps = {
  ariaLabel: string;
  label: string;
  amount?: number | null;
  note: string;
  link: { to: string; label: string };
  children?: ReactNode;
};

export function HeroCard({ ariaLabel, label, amount, note, link, children }: HeroProps) {
  return (
    <section className={classes.hero} aria-label={ariaLabel}>
      <div className={classes.heroMain}>
        <div className={classes.heroTop}>
          <span className={classes.heroLabel}>{label}</span>
          <Link to={link.to} className={classes.heroLink}>
            {link.label}
            <IconChevronLeft size={16} stroke={1.7} aria-hidden />
          </Link>
        </div>
        <div className={classes.heroValue}>
          <strong className={classes.heroNumber}>{faNumber(Math.round(amount ?? 0))}</strong>
          <span className={classes.heroUnit}>ریال</span>
        </div>
        <span className={classes.heroNote}>{note}</span>
      </div>
      {children}
    </section>
  );
}

export function HeroStats({ children }: { children: ReactNode }) {
  return (
    <>
      <div className={classes.divider} />
      <div className={classes.stats}>{children}</div>
    </>
  );
}

type StatProps = { label: string; value: string; suffix?: string; progress?: number; hint?: string };

export function HeroStat({ label, value, suffix, progress, hint }: StatProps) {
  return (
    <div className={classes.stat}>
      <span className={classes.statLabel}>{label}</span>
      <span className={classes.statValue}>
        {value}
        {suffix && <small>{suffix}</small>}
      </span>
      {progress !== undefined ? (
        <span className={classes.bar} aria-hidden>
          <span style={{ width: `${Math.round(progress)}%` }} />
        </span>
      ) : (
        hint && <span className={classes.statHint}>{hint}</span>
      )}
    </div>
  );
}

type BandProps = { to: string; icon: ReactNode; label: string; value: string; action?: ReactNode };

export function HeroBand({ to, icon, label, value, action }: BandProps) {
  return (
    <Link to={to} className={classes.band}>
      {icon}
      <span className={classes.bandText}>
        <span className={classes.bandLabel}>{label}</span>
        <span className={classes.bandValue}>{value}</span>
      </span>
      {action && <span className={classes.bandAction}>{action}</span>}
    </Link>
  );
}

export function HeroSkeleton({ label }: { label: string }) {
  return (
    <section className={classes.hero} aria-label={label} aria-busy="true">
      <span className={classes.shimmer} style={{ width: 74, height: 12 }} />
      <span className={classes.shimmer} style={{ width: 190, height: 28 }} />
      <div className={classes.divider} />
      <div className={classes.stats}>
        {[0, 1, 2].map((item) => (
          <span key={item} className={classes.stat}>
            <span className={classes.shimmer} style={{ width: "70%", height: 10 }} />
            <span className={classes.shimmer} style={{ width: "50%", height: 16 }} />
          </span>
        ))}
      </div>
      <span className={classes.shimmer} style={{ height: 52, borderRadius: 14 }} />
    </section>
  );
}

export function HeroError({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <section className={classes.hero} aria-label={label}>
      <div className={classes.heroMessage}>
        <strong>اطلاعات امروز دریافت نشد</strong>
        <p>اتصال را بررسی کنید و دوباره تلاش کنید. بقیه‌ی بخش‌ها در دسترس‌اند.</p>
        <button type="button" className={classes.heroButton} onClick={onRetry}>
          <IconRefresh size={18} stroke={1.8} aria-hidden />
          تلاش دوباره
        </button>
      </div>
    </section>
  );
}
